import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Send, Banknote, Coins,
  Users, Mail, AlertCircle, CheckCircle2, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminSGA = () => {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sga/status`, auth),
        axios.get(`${API_URL}/api/admin/sga/affiliate-payouts?status_filter=owed`, auth),
      ]);
      setStatus(s.data);
      setPayouts(p.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SGA status');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const enrichNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/sga/enrich-power-agents`, {}, auth);
      toast.success(`${r.data.promoted} new power agents tagged`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enrich failed'); }
    finally { setRunning(false); }
  };

  const seedBanks = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/sga/seed-banks`, {}, auth);
      toast.success(`${r.data.inserted} banks added · ${r.data.skipped} already existed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
    finally { setRunning(false); }
  };

  const runPitch = async () => {
    if (!window.confirm('Send today\'s WhatsApp pitch batch (max 25)?')) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/sga/pitch/run?max_per_run=25`, {}, auth);
      toast.success(`${r.data.sent} pitches sent · ${r.data.skipped || 0} skipped`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Pitch failed'); }
    finally { setRunning(false); }
  };

  const markPaid = async (sessionId) => {
    if (!window.confirm('Confirm this affiliate payout has been transferred to the referrer?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/sga/affiliate-payouts/${sessionId}/mark-paid`, {}, auth);
      toast.success('Payout marked paid');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  if (loading && !status) {
    return (
      <div className="pe-card text-center" style={{ padding: 'var(--pe-space-xl)' }}>
        <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--pe-gold)' }} />
      </div>
    );
  }

  const pa = status?.power_agents || {};
  const ap = status?.affiliate_payouts || {};

  return (
    <div className="space-y-5" data-testid="admin-sga">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="pe-label mb-1">Sovereign Growth Agent</p>
          <h2 className="pe-h2">Power Agent Acquisition Cockpit</h2>
          <p className="pe-body mt-1" style={{ maxWidth: '720px' }}>
            Phase 1 enrichment → Phase 2 personalised WhatsApp pitch (25/day) → Phase 3
            auto Welcome Kit on payment → Phase 4 ₹750 affiliate payouts.
            Bank desks (8 NRI banks) ride the same cycle.
          </p>
        </div>
        <button onClick={load} className="pe-btn pe-btn-ghost" data-testid="sga-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sga-kpis">
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }}>
          <p className="pe-kpi-label">Power Agents</p>
          <p className="pe-kpi-value pe-kpi-value-gold">{pa.total ?? 0}</p>
          <p className="pe-body" style={{ fontSize: '0.7rem', color: 'var(--pe-text-muted)' }}>
            {pa.pitched ?? 0} pitched · {pa.remaining ?? 0} pending
          </p>
        </div>
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }}>
          <p className="pe-kpi-label">Sent today</p>
          <p className="pe-kpi-value">{pa.sent_today ?? 0}<span style={{ fontSize: '1rem', color: 'var(--pe-text-muted)' }}> / {pa.daily_cap ?? 25}</span></p>
        </div>
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }}>
          <p className="pe-kpi-label">Bank desks queued</p>
          <p className="pe-kpi-value">{status?.bank_representatives?.total ?? 0}</p>
        </div>
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }}>
          <p className="pe-kpi-label">Welcome kits sent</p>
          <p className="pe-kpi-value">{status?.welcome_kits_sent ?? 0}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="pe-card flex flex-wrap gap-2" style={{ padding: 'var(--pe-space-md)' }} data-testid="sga-actions">
        <button onClick={enrichNow} disabled={running} className="pe-btn pe-btn-ghost" data-testid="sga-enrich-btn">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
          Enrich power agents
        </button>
        <button onClick={seedBanks} disabled={running} className="pe-btn pe-btn-ghost" data-testid="sga-seed-banks">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Banknote className="h-3.5 w-3.5" />}
          Seed NRI bank desks
        </button>
        <button onClick={runPitch} disabled={running} className="pe-btn pe-btn-primary" data-testid="sga-run-pitch">
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send pitch batch now
        </button>
      </div>

      {/* Last payment */}
      {status?.last_payment && (
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="sga-last-payment">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4" style={{ color: 'var(--pe-success)' }} />
            <p className="pe-label" style={{ margin: 0 }}>Last payment received</p>
          </div>
          <p className="pe-body" style={{ fontSize: '0.95rem' }}>
            <b>{status.last_payment.amount} {(status.last_payment.currency || '').toUpperCase()}</b>
            {' · '}{(status.last_payment.metadata?.purpose) || '—'}
            {' · '}{(status.last_payment.metadata?.user_email) || '—'}
          </p>
        </div>
      )}

      {/* Affiliate payouts owed */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="sga-payouts">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
            <h3 className="pe-h3" style={{ fontSize: '1rem', margin: 0 }}>
              Affiliate payouts owed ({ap.owed_count ?? 0})
            </h3>
          </div>
          <p className="pe-body" style={{ fontSize: '0.8rem', color: 'var(--pe-text-muted)' }}>
            Total value: <b style={{ color: 'var(--pe-text)' }}>{ap.owed_total_value ?? 0}</b>
          </p>
        </div>
        {payouts.length === 0 ? (
          <p className="pe-body" style={{ color: 'var(--pe-text-muted)', textAlign: 'center', padding: 'var(--pe-space-md)' }}>
            No payouts owed. They appear here when a referee pays.
          </p>
        ) : (
          <div className="space-y-2">
            {payouts.map((p) => (
              <div
                key={p.session_id}
                className="pe-surface-2"
                style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}
                data-testid={`sga-payout-${p.session_id}`}
              >
                <div style={{ flex: '1 1 60%', minWidth: '240px' }}>
                  <p className="pe-body" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    Pay <span style={{ color: 'var(--pe-gold)' }}>{p.payout_label}</span> to {p.referrer_name || p.referrer_email}
                  </p>
                  <p className="pe-body" style={{ fontSize: '0.72rem', color: 'var(--pe-text-muted)' }}>
                    Triggered by: {p.referee_email} paid {p.trigger_amount} {(p.payout_currency || '').toUpperCase()} for {p.trigger_purpose}
                  </p>
                </div>
                <button
                  onClick={() => markPaid(p.session_id)}
                  className="pe-btn pe-btn-primary"
                  style={{ padding: '0.3rem 0.7rem', fontSize: '0.7rem' }}
                  data-testid={`sga-payout-mark-paid-${p.session_id}`}
                >
                  Mark paid
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Heads-up */}
      {status?.whatsapp_template === 'sign_up_v2' && (
        <div className="pe-card" style={{ padding: 'var(--pe-space-md)', borderLeft: '3px solid var(--pe-warning)' }} data-testid="sga-template-warning">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--pe-warning)' }} />
            <div>
              <p className="pe-body" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                Pitches use the generic <code>sign_up_v2</code> template
              </p>
              <p className="pe-body" style={{ fontSize: '0.78rem', color: 'var(--pe-text-muted)' }}>
                For NAME / CITY / PORTFOLIO personalisation, submit a Marketing template
                <code> power_agent_pitch_v1</code> via WhatsApp Manager → Templates, then set
                <code> META_WHATSAPP_POWER_TEMPLATE=power_agent_pitch_v1</code> in backend/.env.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email destination */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="sga-email-config">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4" style={{ color: 'var(--pe-gold)' }} />
          <p className="pe-body" style={{ fontSize: '0.85rem' }}>
            Real-time payment alerts go to: <b>{status?.admin_email || '—'}</b>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSGA;
